import { visit } from 'unist-util-visit';
import type { Plugin } from 'unified';
import type { Root, Element } from 'hast';
import { normalizeDate } from './market-data-utils';

const VALID_ASSET_TYPES = ['stock', 'crypto', 'commodity', 'forex'];
const VALID_INTERVALS = ['1d', '1h', '5m'];
const VALID_CHART_TYPES = ['candlestick', 'line'];

/**
 * Rehype plugin to transform <asset> HTML elements into span elements
 * with data attributes that can be handled by the AssetChart component.
 *
 * Follows the same pattern as rehype-chart.ts.
 *
 * NOTE: This plugin duplicates the validation rules from parseAssetTag()
 * in asset-tag-parser.ts rather than calling it, because parseAssetTag()
 * operates on a raw HTML string (regex extraction) while this plugin
 * receives parsed HAST node properties. Both paths must apply identical
 * rules: required symbol/asset_type, interval/chart_type defaults,
 * date normalization via normalizeDate(), and start > end swap.
 * If validation rules change, update both places.
 */
const rehypeAsset: Plugin<[], Root> = () => {
  return (tree: Root) => {
    visit(tree, 'element', (node: Element) => {
      if (node.tagName !== 'asset') return;

      const symbol = node.properties?.symbol as string | undefined;
      const assetType = node.properties?.asset_type as string | undefined;

      // Required validation
      if (!symbol || !assetType || !VALID_ASSET_TYPES.includes(assetType)) return;

      const rawInterval = node.properties?.interval as string | undefined;
      const interval = rawInterval && VALID_INTERVALS.includes(rawInterval) ? rawInterval : '1d';

      const rawChartType = node.properties?.chart_type as string | undefined;
      const chartType = rawChartType && VALID_CHART_TYPES.includes(rawChartType) ? rawChartType : 'candlestick';

      let start = normalizeDate(node.properties?.start as string ?? '') ?? undefined;
      let end = normalizeDate(node.properties?.end as string ?? '') ?? undefined;
      if (start && end && start > end) {
        console.warn(`[rehype-asset] start "${start}" > end "${end}", swapping`);
        [start, end] = [end, start];
      }
      const studies = node.properties?.studies as string | undefined;

      node.tagName = 'span';
      node.properties = {
        dataAsset: true,
        dataAssetSymbol: symbol,
        dataAssetType: assetType,
        dataAssetInterval: interval,
        dataAssetChartType: chartType,
        ...(start && { dataAssetStart: start }),
        ...(end && { dataAssetEnd: end }),
        ...(studies && { dataAssetStudies: studies }),
      };
    });
  };
};

export default rehypeAsset;
